import { useState, useEffect } from 'react';
import { Box, Typography, TextField, Divider } from '@mui/material';
import FirmGuardrailsSkeleton from './FirmGuardrailsSkeleton';
import PrimaryButton from '../Common/Buttons/PrimaryButton';
import { useIRWorkstation } from 'features/IRWorkstation/contexts/IRWorkstationContext';
import { useUserContext } from 'contexts/UserContext';
import { useNotificationContext } from 'contexts/NotificationContext';
import { useTrackEvent } from 'hooks/useTrackEvent';
import { ButtonClickTargets } from 'constants/buttonClickTargets';
import guardrailsService from 'services/guardrailsService';
import {
	validateGuardrails,
	GUARDRAILS_MAX_LENGTH,
} from './guardrailsValidations';

const GUARDRAILS_PLACEHOLDER =
	"e.g. Never state or imply forward looking figures. Never guarantee any returns. Always use the firm's full name and never shorten it";

const FirmGuardrails = () => {
	const { tenant } = useIRWorkstation();
	const { user } = useUserContext();
	const { showNotification } = useNotificationContext();
	const { trackButtonClick } = useTrackEvent({ tenant, product: 'IEM' });

	const [guardrails, setGuardrails] = useState('');
	const [savedGuardrails, setSavedGuardrails] = useState('');
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);

	const validation = validateGuardrails(guardrails);
	const currentGuardrailsValue = validation.value;
	const savedGuardrailsValue = validateGuardrails(savedGuardrails).value;

	const canEdit = user?.role === 'tenant_admin';
	const hasChanges = currentGuardrailsValue !== savedGuardrailsValue;

	// Load the tenant's guardrails and establish the baseline for unsaved changes
	useEffect(() => {
		const fetchGuardrails = async () => {
			if (!tenant?.id) {
				setLoading(false);
				return;
			}

			setLoading(true);

			try {
				const response = await guardrailsService.getGuardrails(tenant.id);
				const value = response?.guardrails ?? '';

				setGuardrails(value);
				setSavedGuardrails(value);
			} catch (err) {
				showNotification(
					err?.message || 'Failed to load firm authoring guardrails',
					'error'
				);
			} finally {
				setLoading(false);
			}
		};

		fetchGuardrails();
	}, [tenant?.id, showNotification]);

	const handleSave = async () => {
		if (
			!tenant?.id ||
			!canEdit ||
			saving ||
			!hasChanges ||
			!validation.isValid
		) {
			return;
		}

		trackButtonClick(ButtonClickTargets.SettingsGuardrailsSave);
		setSaving(true);

		try {
			const response = await guardrailsService.updateGuardrails(
				tenant.id,
				currentGuardrailsValue
			);

			const value = response?.guardrails ?? '';

			setGuardrails(value);
			setSavedGuardrails(value);

			showNotification(
				'Firm authoring guardrails saved successfully',
				'success'
			);
		} catch (err) {
			showNotification(
				err?.message || 'Failed to save firm authoring guardrails',
				'error'
			);
		} finally {
			setSaving(false);
		}
	};

	if (loading) {
		return <FirmGuardrailsSkeleton showButton={canEdit} />;
	}

	return (
		<Box
			sx={{
				backgroundColor: 'background.paper',
				border: '0.5px solid',
				borderColor: 'border.light',
				borderRadius: '8px',
				overflow: 'hidden',
			}}
		>
			<Box
				sx={{
					display: 'flex',
					alignItems: 'flex-start',
					justifyContent: 'space-between',
					gap: '16px',
					padding: '10px 12px',
				}}
			>
				<Box>
					<Typography
						sx={{
							fontSize: '13px',
							fontWeight: 600,
							color: 'text.primary',
						}}
					>
						Firm Authoring & Guardrails
					</Typography>

					<Typography
						sx={{
							fontSize: '11px',
							color: 'text.secondary',
							mt: '2px',
						}}
					>
						Set firm-wide instructions on what is and isn&apos;t allowed in
						generated correspondence across the Investor Engagement Manager
					</Typography>
				</Box>

				{canEdit && (
					<PrimaryButton
						label={hasChanges ? 'Save changes' : 'No changes'}
						onClick={handleSave}
						loading={saving}
						disabled={saving || !hasChanges || !validation.isValid}
					/>
				)}
			</Box>

			<Divider />

			<Box sx={{ padding: '12px' }}>
				<TextField
					fullWidth
					multiline
					minRows={5}
					maxRows={5}
					value={guardrails}
					onChange={(event) => setGuardrails(event.target.value)}
					disabled={saving}
					placeholder={GUARDRAILS_PLACEHOLDER}
					error={!validation.isValid}
					helperText={
						validation.errorMessage
							? `${validation.errorMessage} (${validation.characterCount} / ${GUARDRAILS_MAX_LENGTH})`
							: `${validation.characterCount} / ${GUARDRAILS_MAX_LENGTH}`
					}
					slotProps={{
						htmlInput: {
							readOnly: !canEdit,
						},
					}}
					sx={{
						'& textarea': {
							resize: 'vertical',
						},
					}}
				/>
			</Box>
		</Box>
	);
};

export default FirmGuardrails;
